<script lang="ts">
  import { router } from "@inertiajs/svelte";
  import TripSubNav from "../../components/navigation/TripSubNav.svelte";
  import Button from "../../components/input/Button.svelte";
  import ConfirmDialog from "../../components/overlay/ConfirmDialog.svelte";
  import EmptyState from "../../components/feedback/EmptyState.svelte";
  import { formatCurrency } from "../../lib/utils/format";

  type Booking = {
    id: string;
    transport_type: string;
    departure_location: string;
    arrival_location: string;
    departure_at?: string;
    arrival_at?: string;
    carrier_name?: string;
    carrier_number?: string;
    booking_reference?: string;
    price_cents?: number;
    currency?: string;
    status?: string;
    seat_info?: string;
  };

  let { trip, bookings = [] }: { trip: any; bookings?: Booking[] } = $props();

  const TYPES = [
    { value: "all", label: "전체", icon: "🚦" },
    { value: "train", label: "기차", icon: "🚄" },
    { value: "bus", label: "버스", icon: "🚌" },
    { value: "ferry", label: "페리", icon: "⛴️" },
    { value: "car", label: "자동차", icon: "🚗" },
    { value: "rental", label: "렌터카", icon: "🔑" },
    { value: "ship", label: "선박", icon: "🚢" },
  ];

  let localBookings = $state<Booking[]>([]);
  let bookingsHydrated = false;
  let typeFilter = $state("all");
  let showForm = $state(false);
  let submitting = $state(false);
  let confirmDeleteId = $state<string | null>(null);
  let confirmOpen = $state(false);

  let form = $state({
    transport_type: "train",
    departure_location: "",
    arrival_location: "",
    departure_at: "",
    arrival_at: "",
    carrier_name: "",
    carrier_number: "",
    booking_reference: "",
    seat_info: "",
    price_cents: "",
  });

  function csrfToken(): string {
    return document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "";
  }

  $effect(() => {
    if (bookingsHydrated) return;
    localBookings = [...bookings];
    bookingsHydrated = true;
  });

  const now = new Date();
  const upcomingBookings = $derived(
    localBookings
      .filter((b) => b.departure_at && new Date(b.departure_at) > now)
      .sort((a, b) => new Date(a.departure_at!).getTime() - new Date(b.departure_at!).getTime())
      .slice(0, 3)
  );

  const filteredBookings = $derived(
    typeFilter === "all"
      ? localBookings
      : localBookings.filter((b) => b.transport_type === typeFilter)
  );

  async function createBooking() {
    submitting = true;
    const res = await fetch(`/api/v1/trips/${trip.id}/transportation_bookings`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken() },
      body: JSON.stringify({
        transportation_booking: {
          ...form,
          price_cents: form.price_cents ? Number(form.price_cents) : null,
        },
      }),
    });
    submitting = false;
    if (!res.ok) return;
    const json = await res.json();
    const newBooking = json.data || json;
    localBookings = [...localBookings, newBooking];
    showForm = false;
    form = { transport_type: "train", departure_location: "", arrival_location: "", departure_at: "", arrival_at: "", carrier_name: "", carrier_number: "", booking_reference: "", seat_info: "", price_cents: "" };
  }

  function askDelete(id: string) {
    confirmDeleteId = id;
    confirmOpen = true;
  }

  async function removeBooking() {
    if (!confirmDeleteId) return;
    await fetch(`/api/v1/trips/${trip.id}/transportation_bookings/${confirmDeleteId}`, {
      method: "DELETE",
      headers: { "X-CSRF-Token": csrfToken() },
    });
    localBookings = localBookings.filter((b) => b.id !== confirmDeleteId);
    confirmDeleteId = null;
  }

  function typeIcon(type: string): string {
    return TYPES.find((t) => t.value === type)?.icon ?? "🚦";
  }
</script>

<TripSubNav {trip} currentPage="transport" />

<div class="mx-auto max-w-5xl space-y-6 px-4 py-6 sm:px-6 lg:px-8">
  <!-- Header -->
  <div class="flex items-center justify-between gap-4">
    <div>
      <h1 class="text-2xl font-bold text-white">이동 예약</h1>
      <p class="mt-1 text-sm text-muted">기차, 버스, 페리, 차량 이동 예약을 관리합니다.</p>
    </div>
    <Button variant="primary" size="sm" onclick={() => (showForm = true)}>+ 예약 추가</Button>
  </div>

  <!-- Upcoming -->
  {#if upcomingBookings.length > 0}
    <section class="rounded-2xl border border-teal-400/20 bg-teal-400/5 p-4">
      <p class="mb-3 text-xs font-semibold uppercase tracking-wider text-teal-400">다가오는 이동</p>
      <div class="space-y-2">
        {#each upcomingBookings as b}
          <div class="flex items-center gap-3">
            <span class="text-xl">{typeIcon(b.transport_type)}</span>
            <div class="min-w-0 flex-1">
              <p class="text-sm font-medium text-white truncate">{b.departure_location} → {b.arrival_location}</p>
              <p class="text-xs text-muted">{new Date(b.departure_at!).toLocaleString("ko-KR", { month: "numeric", day: "numeric", hour: "2-digit", minute: "2-digit" })}</p>
            </div>
            {#if b.booking_reference}
              <span class="text-xs text-muted">{b.booking_reference}</span>
            {/if}
          </div>
        {/each}
      </div>
    </section>
  {/if}

  <!-- Type filter tabs -->
  <div class="flex gap-2 overflow-x-auto pb-1">
    {#each TYPES as t}
      <button
        type="button"
        onclick={() => (typeFilter = t.value)}
        class="shrink-0 flex items-center gap-1.5 rounded-xl px-3 py-2 text-sm font-medium transition-colors
          {typeFilter === t.value ? 'bg-teal-400/15 text-teal-400 border border-teal-400/30' : 'border border-white/10 text-muted hover:text-secondary'}"
      >
        <span>{t.icon}</span>
        <span>{t.label}</span>
        {#if t.value !== "all"}
          <span class="text-xs opacity-60">({localBookings.filter((b) => b.transport_type === t.value).length})</span>
        {/if}
      </button>
    {/each}
  </div>

  <!-- Booking list -->
  <div class="space-y-3">
    {#if filteredBookings.length === 0}
      <EmptyState icon={typeFilter === "all" ? "🚦" : typeIcon(typeFilter)} title="예약 없음" description="등록된 이동 예약이 없습니다." />
    {:else}
      {#each filteredBookings as booking (booking.id)}
        <article class="rounded-2xl border border-white/10 bg-slate-900/60 p-4">
          <div class="flex items-start justify-between gap-4">
            <div class="flex items-start gap-3 min-w-0">
              <span class="text-2xl shrink-0 mt-0.5">{typeIcon(booking.transport_type)}</span>
              <div class="min-w-0 space-y-1">
                <div class="flex flex-wrap items-center gap-2">
                  <p class="font-semibold text-white">{booking.departure_location} → {booking.arrival_location}</p>
                  {#if booking.status}
                    <span class="rounded-full bg-white/8 px-2 py-0.5 text-xs text-muted">{booking.status}</span>
                  {/if}
                </div>
                <p class="text-sm text-muted">
                  {booking.departure_at ? new Date(booking.departure_at).toLocaleString("ko-KR", { month: "numeric", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "출발 미정"}
                  {#if booking.arrival_at}
                    <span class="text-secondary"> → </span>
                    {new Date(booking.arrival_at).toLocaleTimeString("ko-KR", { hour: "2-digit", minute: "2-digit" })}
                  {/if}
                </p>
                <p class="text-xs text-muted">
                  {booking.carrier_name || ""}{booking.carrier_number ? ` ${booking.carrier_number}` : ""}
                  {booking.booking_reference ? ` · 예약번호 ${booking.booking_reference}` : ""}
                  {booking.seat_info ? ` · ${booking.seat_info}` : ""}
                </p>
              </div>
            </div>
            <div class="flex items-center gap-3 shrink-0">
              {#if booking.price_cents}
                <span class="text-sm text-muted">{formatCurrency(booking.price_cents, booking.currency || "KRW")}</span>
              {/if}
              <button
                type="button"
                class="text-xs text-rose-400 hover:text-rose-300 transition-colors"
                onclick={() => askDelete(booking.id)}
              >삭제</button>
            </div>
          </div>
        </article>
      {/each}
    {/if}
  </div>
</div>

<!-- Add Booking Modal -->
{#if showForm}
  <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
    <div class="w-full max-w-lg rounded-3xl border border-white/10 bg-slate-950 p-6">
      <div class="mb-5 flex items-center justify-between">
        <h2 class="text-lg font-semibold">예약 추가</h2>
        <button type="button" class="text-muted hover:text-primary" onclick={() => (showForm = false)}>✕</button>
      </div>
      <div class="space-y-3">
        <div>
          <label for="transport-type" class="text-xs text-muted mb-1 block">교통수단</label>
          <select id="transport-type" bind:value={form.transport_type} class="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-white">
            {#each TYPES.slice(1) as t}
              <option value={t.value}>{t.icon} {t.label}</option>
            {/each}
          </select>
        </div>
        <div class="grid grid-cols-2 gap-3">
          <input bind:value={form.departure_location} class="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-white placeholder-muted outline-none focus:border-teal-400" placeholder="출발지 *" />
          <input bind:value={form.arrival_location} class="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-white placeholder-muted outline-none focus:border-teal-400" placeholder="도착지 *" />
        </div>
        <div class="grid grid-cols-2 gap-3">
          <div>
            <label for="transport-departure-at" class="text-xs text-muted mb-1 block">출발</label>
            <input id="transport-departure-at" bind:value={form.departure_at} type="datetime-local" class="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-white outline-none focus:border-teal-400" />
          </div>
          <div>
            <label for="transport-arrival-at" class="text-xs text-muted mb-1 block">도착</label>
            <input id="transport-arrival-at" bind:value={form.arrival_at} type="datetime-local" class="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-white outline-none focus:border-teal-400" />
          </div>
        </div>
        <div class="grid grid-cols-2 gap-3">
          <input bind:value={form.carrier_name} class="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-white placeholder-muted outline-none focus:border-teal-400" placeholder="운송사" />
          <input bind:value={form.carrier_number} class="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-white placeholder-muted outline-none focus:border-teal-400" placeholder="편명/번호" />
        </div>
        <div class="grid grid-cols-2 gap-3">
          <input bind:value={form.booking_reference} class="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-white placeholder-muted outline-none focus:border-teal-400" placeholder="예약번호" />
          <input bind:value={form.seat_info} class="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-white placeholder-muted outline-none focus:border-teal-400" placeholder="좌석 정보" />
        </div>
        <input bind:value={form.price_cents} type="number" min="0" class="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-white placeholder-muted outline-none focus:border-teal-400" placeholder="금액 (원)" />
        <Button variant="primary" fullWidth loading={submitting} onclick={createBooking}>저장</Button>
      </div>
    </div>
  </div>
{/if}

<ConfirmDialog
  bind:open={confirmOpen}
  title="예약 삭제"
  message="이 이동 예약을 삭제하시겠습니까?"
  confirmLabel="삭제"
  onconfirm={removeBooking}
/>
